import React, { useState } from 'react';
import Modal from '../../../shared/modal';
import Button from '../../../shared/button';
import { faExchangeAlt } from '@fortawesome/pro-solid-svg-icons';
import Toggle from '../../toggle';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OverlayTrigger, Tooltip } from 'react-bootstrap-5';

type PartnerConnectionModalProps = {
  allowManualSignups: boolean;
  allowNoCredSignups: boolean;
  connected: boolean;
  modalOpen: boolean;
  noCredSignup: boolean;
  partnerName: string;
  sendingRequest: boolean;
  onClose: () => void;
  onConfirm: (credentials?: { username: string; password: string }) => void;
  toggleNoCredRegistration: () => void;
}

export default function PartnerConnectionModal({
  allowManualSignups,
  allowNoCredSignups,
  partnerName,
  onClose,
  onConfirm,
  modalOpen,
  noCredSignup,
  sendingRequest,
  toggleNoCredRegistration,
  connected
}: PartnerConnectionModalProps) {
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });

  const updateCredentials = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const manuallyConnectPartner = (e) => {
    e.preventDefault();
    onConfirm(credentials);
  };

  const closeModal = () => onClose();

  const automaticRegistrationToggle = () => {
    return (
      <Toggle
        checked={noCredSignup}
        disabled={!allowManualSignups}
        showText={false}
        theme="ds"
        onChange={toggleNoCredRegistration}
      />
    );
  };
  const manualSignupText = `Sign in with your ${partnerName} username and password and all syndicated listings will be \
                            sent to ${partnerName}.`;
  const noCredSignUpText = `Click Connect and we will automatically register you with ${partnerName} and start \
                               sending all syndicated listings to them.`;
  const disableManualSignupTooltip = () => {
    const tooltip = <Tooltip id="manual-signup-tooltip">
      {partnerName} does not support registration with credentials.
    </Tooltip>;

    return (
      <OverlayTrigger
        overlay={tooltip}
        placement="top"
      >
        <span>
          {automaticRegistrationToggle()}
        </span>
      </OverlayTrigger>
    );
  };

  const renderConnectForm = () => {
    return (
      <React.Fragment>
        {allowNoCredSignups && (
          <React.Fragment>
            <div className="d-flex align-items-center justify-content-center mb-4 gap-3">
              {
                allowManualSignups ? (
                  <Toggle
                    checked={noCredSignup}
                    showText={false}
                    theme="ds"
                    onChange={toggleNoCredRegistration}
                  />
                ) : disableManualSignupTooltip()
              }
              <div className="text-center">Automatically register without credentials</div>
            </div>
            { allowManualSignups && !noCredSignup && (
              <div className="d-flex align-items-center justify-content-between gap-4 mb-4">
                <hr className="flex-grow-1 m-0"/>
                <div>OR</div>
                <hr className="flex-grow-1 m-0"/>
              </div>
            )}
          </React.Fragment>
        )}
        {noCredSignup && (
          <React.Fragment>
            <div className="text-center">{noCredSignUpText}</div>
          </React.Fragment>
        )}
        {!noCredSignup && (
          <React.Fragment>
            <div className="text-center">{manualSignupText}</div>
            <form className="d-flex flex-column gap-3" onSubmit={manuallyConnectPartner}>
              <div className="d-flex flex-column gap-1">
                <label
                  className="form-label"
                  htmlFor="username"
                >
                  Username
                </label>
                <input
                  className="form-control"
                  id="username"
                  name="username"
                  placeholder="Enter your username"
                  required
                  type="text"
                  value={credentials.username}
                  onChange={updateCredentials}
                />
              </div>

              <div className="d-flex flex-column gap-1">
                <label
                  className="form-label"
                  htmlFor="password"
                >
                  Password
                </label>
                <input
                  className="form-control"
                  id="password"
                  name="password"
                  placeholder="Enter your password"
                  required
                  type="password"
                  value={credentials.password}
                  onChange={updateCredentials}
                />
              </div>
            </form>
          </React.Fragment>
        )}
        <Modal.Footer footerOrientation="center">
          <Button.Cancel className="me-3" onClick={closeModal}>
            Cancel
          </Button.Cancel>
          <Button.Primary disabled={sendingRequest} onClick={manuallyConnectPartner}>
            {sendingRequest && <FontAwesomeIcon className="me-2" icon={faSpinner} spin/>}
            {sendingRequest ? 'Connecting...' : 'Connect'}
          </Button.Primary>
        </Modal.Footer>
      </React.Fragment>
    );
  };

  const renderDisconnectForm = () => {
    return (
      <React.Fragment>
        <div className="text-center">
          Are you sure you would like to disconnect from {partnerName}?
        </div>
        <Modal.Footer footerOrientation="center">
          <Button.Cancel
            className="me-3"
            onClick={closeModal}
          >
            Cancel
          </Button.Cancel>
          <Button.Danger disabled={sendingRequest} onClick={onConfirm}>
            {sendingRequest && <FontAwesomeIcon className="me-2" icon={faSpinner} spin/>}
            {sendingRequest ? 'Disconnecting...' : 'Disconnect'}
          </Button.Danger>
        </Modal.Footer>
      </React.Fragment>
    );
  };

  return (
    <Modal
      icon={faExchangeAlt}
      show={modalOpen}
      title={`${connected ? 'Disconnect from' : 'Connect to'} ${partnerName}`}
      onHide={closeModal}
    >
      {connected ? renderDisconnectForm() : renderConnectForm()}
    </Modal>
  );
}
